import { createElement, type ReactNode } from "react";
import { Link } from "react-router";
import { FEATURE_FLAGS } from "./feature-flag-constants";
import AskVivoIcon from "../assets/icons/ask_vivo2.svg?react";
import SubjectsIcon from "../assets/icons/subjects.svg?react";
import SafetyIcon from "../assets/icons/safety.svg?react";
import ClinicalIcon from "../assets/icons/clinical.svg?react";
import DataIcon from "../assets/icons/data.svg?react";
import SitesIcon from "../assets/icons/sites.svg?react";
import RiskIcon from "../assets/icons/risk.svg?react";

import GraphIcon from "../assets/icons/graph.svg?react";
import InsightsIcon from "../assets/icons/insights.svg?react";
import AlertsMenuItem from "../components/Alerts/AlertsMenuItem";
import AlertsMenuIcon from "../components/Alerts/AlertsMenuIcon";
import { ActionsNavLabel } from "../components/Alerts/ActionsNavLabel";
import HomeIcon from "../assets/icons/home.svg?react";
import PlanOfRecordIcon from "../assets/icons/portfolio_milestones.svg?react";
import ChecklistIcon from "../assets/icons/checklist.svg?react";
import ContactsIcon from "../assets/icons/contact_phone.svg?react";
import LogisticsIcon from "../assets/icons/globe.svg?react";

interface MenuItem {
  key: string;
  icon?: ReactNode;
  label: ReactNode;
  featureflag?: string | string[];
  children?: MenuItem[];
}

export const getMenuItems = (collapsed: boolean = false): MenuItem[] => [
  {
    featureflag: FEATURE_FLAGS.TRIAL_HOME_SCREEN,
    key: "/home",
    icon: <HomeIcon />,
    label: <Link to={"home"}>Home</Link>,
  },
  {
    featureflag: FEATURE_FLAGS.ASKVIVO,
    key: "/askvivo",
    icon: <AskVivoIcon />,
    label: <Link to={"askvivo"}>Ask Vivo</Link>,
  },
  {
    key: "/plan",
    icon: <PlanOfRecordIcon width={24} height={24} />,
    label: <span data-testid="trial-nav-section-plan">Plan</span>,
    children: [
      {
        featureflag: FEATURE_FLAGS.STUDY_DOCUMENT_WORKSPACE,
        key: "/plan/study-documents",
        label: (
          <Link
            data-testid="trial-nav-link-study-documents"
            to={"plan/study-documents"}
          >
            Study Documents
          </Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.PLAN_OF_RECORD,
        key: "/plan/milestones",
        label: (
          <Link
            data-testid="trial-nav-link-plan-milestones"
            to={"plan/milestones"}
          >
            Milestones
          </Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.TRIAL_ENROLLMENT,
        key: "/plan/enrollment",
        label: <Link to={"plan/enrollment"}>Enrollment</Link>,
      },
    ],
  },
  {
    key: "/insights",
    featureflag: FEATURE_FLAGS.INSIGHTS,
    icon: <InsightsIcon />,
    label: <Link to={"insights"}>Insights</Link>,
  },
  {
    key: "/alerts",
    featureflag: FEATURE_FLAGS.ALERTS,
    icon: <AlertsMenuIcon collapsed={collapsed} />,
    label: <AlertsMenuItem />,
  },
  {
    key: "/actions",
    featureflag: FEATURE_FLAGS.ACTIONS,
    // checklist.svg is drawn on a 20-unit grid (it is shared with the Ask Vivo
    // suggested-question icons, which are a 20x20 set). Every other nav icon is
    // 24x24, and antd only sets `min-width: 14px` on the icon slot, so the
    // intrinsic width decides where the label starts — at 20px wide the Actions
    // label sat 4px left of every other one. Render it at 24 here only, the same
    // way alert2.svg is a 20-unit drawing sized to 24 for the nav.
    icon: <ChecklistIcon width={24} height={24} />,
    label: <ActionsNavLabel />,
  },
  {
    featureflag: FEATURE_FLAGS.SITES_OVERVIEW,
    key: "/sites/overview",
    icon: createElement(SitesIcon),
    label: (
      <Link data-testid="trial-nav-link-sites-overview" to={"sites/overview"}>
        Sites
      </Link>
    ),
  },
  {
    featureflag: FEATURE_FLAGS.DOCUMENTS,
    key: "/documents",
    icon: createElement(DataIcon),
    label: (
      <Link data-testid="trial-nav-link-documents" to={"documents"}>
        Documents
      </Link>
    ),
  },
  {
    key: "/subjects",
    icon: <SubjectsIcon />,
    label: <span data-testid="trial-nav-section-subjects">Subjects</span>,
    children: [
      {
        featureflag: FEATURE_FLAGS.SUBJECTS_OVERVIEW,
        key: "/subjects/overview",
        label: (
          <Link
            data-testid="trial-nav-link-subjects-participant-profiles"
            to={"subjects/overview"}
          >
            Participant Profiles
          </Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.VISITS_ACTIVITIES,
        key: "/subjects/visits-activities",
        label: (
          <Link to={"subjects/visits-activities"}>Visits & Activities</Link>
        ),
      },
    ],
  },
  {
    key: "/safety",
    icon: <SafetyIcon />,
    label: <span data-testid="trial-nav-section-safety">Safety</span>,
    children: [
      {
        featureflag: FEATURE_FLAGS.ADVERSE_EVENTS,
        key: "/safety/adverse-events",
        label: <Link to={"safety/adverse-events"}>Adverse Events</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.CONMEDS,
        key: "/safety/concomitant-medications",
        label: (
          <Link to={"safety/concomitant-medications"}>Con Medications</Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.CONCOMITANT_PROCEDURES,
        key: "/safety/concomitant-procedures",
        label: <Link to={"safety/concomitant-procedures"}>Con Procedures</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.DOSING_TRACKER,
        key: "/safety/dosing-tracker",
        label: <Link to={"safety/dosing-tracker"}>Dosing Tracker</Link>,
      },

      {
        featureflag: FEATURE_FLAGS.IP_ADMINISTRATION,
        key: "/safety/ip-administration",
        label: <Link to={"safety/ip-administration"}>IP Administration</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.IP_DISPENSATION,
        key: "/safety/ip-dispensation",
        label: <Link to={"safety/ip-dispensation"}>IP Dispensation</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.PHARMACOKINETICS,
        key: "/safety/pharmacokinetics",
        label: (
          <Link
            data-testid="trial-nav-link-safety-pharmacokinetics"
            to={"safety/pharmacokinetics"}
          >
            Pharmacokinetics
          </Link>
        ),
      },
    ],
  },
  {
    key: "/measures",
    icon: <ClinicalIcon />,
    label: <span data-testid="trial-nav-section-measures">Measures</span>,
    children: [
      {
        featureflag: FEATURE_FLAGS.ADMINISTRATIVE_FORM_RECORDS,
        key: "/measures/administrative-form-records",
        label: (
          <Link to={"measures/administrative-form-records"}>
            Admin Form Records
          </Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.SAFETY_MEASURES,
        key: "/measures/assessments",
        label: <Link to={"measures/assessments"}>Assessments</Link>,
      },
      {
        // ECG's dedicated home — numeric intervals (HR/PR/QRS/QT/QTcF) plus the
        // ECG Trends waveform view, consolidated onto one screen. Separated from
        // radiology "Imaging" now that ECG no longer rides the imaging_studies list.
        featureflag: FEATURE_FLAGS.ECG,
        key: "/measures/ecg",
        label: (
          <Link data-testid="trial-nav-link-measures-ecg" to={"measures/ecg"}>
            ECG
          </Link>
        ),
      },
      {
        // Standard CRF Imaging screen. imaging-viewer tenants (demo, Clario)
        // get CT/Endoscopy below instead — modality-scoped since the two have
        // nothing in common to browse together.
        featureflag: FEATURE_FLAGS.IMAGING,
        key: "/measures/imaging",
        label: (
          <Link
            data-testid="trial-nav-link-measures-imaging"
            to={"measures/imaging"}
          >
            Imaging
          </Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.IMAGING_VIEWER,
        key: "/measures/imaging-ct",
        label: (
          <Link
            data-testid="trial-nav-link-measures-imaging-ct"
            to={"measures/imaging-ct"}
          >
            CT
          </Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.IMAGING_VIEWER,
        key: "/measures/imaging-endoscopy",
        label: (
          <Link
            data-testid="trial-nav-link-measures-imaging-endoscopy"
            to={"measures/imaging-endoscopy"}
          >
            Endoscopy
          </Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.IMAGING_STUDIES,
        key: "/measures/imaging-studies",
        label: <Link to={"measures/imaging-studies"}>Imaging Studies</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.CLARIO_IMAGING_WORKFLOW,
        key: "/measures/imaging-reads",
        label: <Link to={"measures/imaging-reads"}>Imaging Reads</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.CLARIO_IMAGING_WORKFLOW,
        key: "/measures/imaging-operations",
        label: (
          <Link to={"measures/imaging-operations"}>Imaging Operations</Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.LABS,
        key: "/measures/labs",
        label: <Link to={"measures/labs"}>Labs</Link>,
      },

      {
        featureflag: FEATURE_FLAGS.EFFICACY_MEASURE,
        key: "/measures/questionnaires",
        label: <Link to={"measures/questionnaires"}>Questionnaires</Link>,
      },
      {
        // Scientific-plausibility review of the COA/PRO (Questionnaires) and
        // quantitative (Assessments) data above — a clinical-science lens on
        // this same measurement data, not a Safety or Data-management screen.
        featureflag: FEATURE_FLAGS.CLINICAL_DATA_REVIEW,
        key: "/measures/clinical-review",
        label: (
          <Link
            data-testid="trial-nav-link-measures-clinical-review"
            to={"measures/clinical-review"}
          >
            Clinical Review
          </Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.WAVEFORMS,
        key: "/measures/spirometry",
        label: <Link to={"measures/spirometry"}>Spirometry</Link>,
      },
      // ECG Trends is no longer a standalone nav entry — it now lives as a view
      // inside the consolidated ECG screen (see "/measures/ecg" above). The
      // /measures/ecg-trends route is kept for backward-compatible deep links.
    ],
  },
  {
    // Phase-B oncology analytics (Analysis) — merged from four slices. No parent
    // flag: each child gates on its own flag, so the section shows whichever
    // analyses are granted (all four for DEMO-ONC-2026-01).
    key: "/analysis",
    icon: createElement(GraphIcon),
    label: <span data-testid="trial-nav-section-analysis">Analysis</span>,
    children: [
      {
        featureflag: FEATURE_FLAGS.TUMOR_RESPONSE,
        key: "/analysis/tumor-response",
        label: <Link to={"analysis/tumor-response"}>Tumor Response</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.BIOMARKERS,
        key: "/analysis/biomarkers",
        label: (
          <Link to={"analysis/biomarkers"}>Biomarkers &amp; Genomics</Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.PHARMACOKINETICS,
        key: "/analysis/pharmacokinetics",
        label: (
          <Link
            data-testid="trial-nav-link-analysis-pharmacokinetics"
            to={"analysis/pharmacokinetics"}
          >
            Pharmacokinetics
          </Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.IMMUNOGENICITY,
        key: "/analysis/immunogenicity",
        label: (
          <Link
            data-testid="trial-nav-link-analysis-immunogenicity"
            to={"analysis/immunogenicity"}
          >
            Immunogenicity
          </Link>
        ),
      },
    ],
  },
  {
    key: "/logistics",
    icon: createElement(LogisticsIcon),
    featureflag: FEATURE_FLAGS.LOGISTICS,
    label: <span data-testid="trial-nav-section-logistics">Logistics</span>,
    children: [
      {
        featureflag: FEATURE_FLAGS.LOGISTICS_MATERIALS,
        key: "/logistics/materials",
        label: <Link to={"logistics/materials"}>Materials</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.LOGISTICS_LOTS,
        key: "/logistics/lots",
        label: <Link to={"logistics/lots"}>Lots</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.LOGISTICS_LOCATIONS,
        key: "/logistics/locations",
        label: <Link to={"logistics/locations"}>Locations</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.LOGISTICS_UNITS,
        key: "/logistics/units",
        label: <Link to={"logistics/units"}>Units</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.LOGISTICS_SHIPMENTS,
        key: "/logistics/shipments",
        label: <Link to={"logistics/shipments"}>Shipments</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.LOGISTICS_SHIPMENT_ITEMS,
        key: "/logistics/shipment-items",
        label: <Link to={"logistics/shipment-items"}>Shipment Items</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.LOGISTICS_INVENTORY,
        key: "/logistics/inventory",
        label: <Link to={"logistics/inventory"}>Inventory</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.LOGISTICS_RECONCILIATION,
        key: "/logistics/reconciliation",
        label: <Link to={"logistics/reconciliation"}>Reconciliation</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.LOGISTICS_EVENTS,
        key: "/logistics/events",
        label: <Link to={"logistics/events"}>Chain of Custody</Link>,
      },
    ],
  },
  {
    key: "/risk",
    icon: <RiskIcon />,
    featureflag: FEATURE_FLAGS.RISKS,
    label: "Quality",
    children: [
      {
        key: "/risk/analytics",
        label: <Link to={"risk/analytics"}>Analytics</Link>,
      },
      {
        key: "/risk/risks",
        label: <Link to={"risk/risks"}>Risks</Link>,
      },
      {
        key: "/risk/signals",
        label: <Link to={"risk/signals"}>Signals</Link>,
      },
      {
        key: "/risk/issues",
        label: <Link to={"risk/issues"}>Breaches</Link>,
      },
      {
        key: "/risk/tasks",
        label: <Link to={"risk/tasks"}>Tasks</Link>,
      },
    ],
  },
  {
    key: "/data",
    icon: <DataIcon />,
    label: <span data-testid="trial-nav-section-data">Data</span>,
    children: [
      {
        featureflag: FEATURE_FLAGS.DATA_CONNECTIONS,
        key: "/data/configuration",
        label: <Link to={"data/configuration"}>Configuration</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.TRIAL_DEFINITIONS,
        key: "/data/definitions",
        label: <Link to={"data/definitions"}>Definitions in Force</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.DATA_DISCREPANCIES,
        key: "/data/discrepancies",
        label: (
          <Link to={"data/discrepancies"}>
            <div>Discrepancies</div>
          </Link>
        ),
      },
      {
        featureflag: FEATURE_FLAGS.STUDY_DESIGN,
        key: "/data/study-design",
        label: <Link to={"data/study-design"}>Study Design</Link>,
      },
      {
        featureflag: FEATURE_FLAGS.SOURCE_AUDIT,
        key: "/data/audit-trail",
        label: <Link to={"data/audit-trail"}>Audit Trail</Link>,
      },
    ],
  },
  {
    key: "/sites/reg-docs-tracker",
    featureflag: FEATURE_FLAGS.REG_DOCS_TRACKER,
    icon: <SitesIcon />,
    label: <Link to={"sites/reg-docs-tracker"}>Reg Docs Tracker</Link>,
  },
  {
    key: "/sites/site-feasibility-board",
    featureflag: FEATURE_FLAGS.SITE_FEASIBILITY_BOARD,
    icon: <SitesIcon />,
    label: (
      <Link to={"sites/site-feasibility-board"}>Site Feasibility Board</Link>
    ),
  },
  {
    key: "/trial-contact-list",
    featureflag: FEATURE_FLAGS.TRIAL_CONTACT_LIST,
    icon: <ContactsIcon />,
    label: <Link to={"trial-contact-list"}>Trial Contact List</Link>,
  },
];
